import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import MyTasks from "../components/MyTasks";
import { useConfig } from "../lib/useConfig";
import { useLang } from "../lib/i18n";
import { KpiCard, PageHeader, pct, num } from "../lib/ui";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { FlaskConical, TestTube2, CheckCircle2, Percent, Clock, XCircle, Send, Eye } from "lucide-react";

const Card = ({ title, children }) => (
  <div className="bg-white border border-slate-200/80 rounded-md shadow-sm p-5">
    <h3 className="text-sm font-semibold text-slate-800 mb-4">{title}</h3>{children}
  </div>
);

export default function LabDashboard() {
  const [filters, setFilters] = useState({});
  const { t } = useLang();
  const { data: cfg } = useConfig();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["lab-dash", filters],
    queryFn: async () => (await api.get("/lab/dashboard", { params: filters })).data,
  });
  const { data: reps } = useQuery({
    queryKey: ["lab-reports", filters],
    queryFn: async () => (await api.get("/lab/reports", { params: { ...filters, limit: 30 } })).data,
  });
  const actMap = useMemo(() => Object.fromEntries(((cfg?.lab_activities) || []).map((a) => [a.code, a.name])), [cfg]);
  const k = data?.kpis || {};
  const positivity = useMemo(() => (data?.by_disease || []).map((d) => ({
    name: t(d.name), positivite: d.tested ? Math.round((d.positive / d.tested) * 100) : 0, tested: d.tested,
  })).filter((d) => d.tested > 0).slice(0, 8), [data, t]);

  return (
    <div data-testid="lab-dashboard-page">
      <PageHeader title={t("Laboratoire · Tableau de Bord")} subtitle={t("Pilier Laboratoire · partage la même géographie et le même moteur d'indicateurs")}>
        <Button className="bg-[#0284C7] hover:bg-[#0369A1]" onClick={() => navigate("/lab/new")} data-testid="lab-new-btn"><FlaskConical className="h-4 w-4 mr-1" /> {t("Nouveau rapport labo")}</Button>
      </PageHeader>
      <MyTasks />
      <GlobalFilters value={filters} onChange={setFilters} />

      {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard testid="lab-received" label={t("Échantillons reçus")} value={num(k.samples_received)} icon={TestTube2} tone="teal" />
            <KpiCard testid="lab-tested" label={t("Analysés")} value={num(k.samples_tested)} sub={`${t("Taux")} ${pct(k.testing_rate)}`} icon={CheckCircle2} tone="teal" />
            <KpiCard testid="lab-positivity" label={t("Taux de positivité")} value={pct(k.positivity_rate)} icon={Percent} tone="red" />
            <KpiCard testid="lab-rejected" label={t("Rejetés")} value={num(k.samples_rejected)} sub={`${t("Taux")} ${pct(k.rejection_rate)}`} icon={XCircle} tone="amber" />
            <KpiCard testid="lab-transmitted" label={t("Résultats transmis")} value={num(k.results_transmitted)} sub={`${t("Taux")} ${pct(k.transmission_rate)}`} icon={Send} />
            <KpiCard testid="lab-tat" label={t("Délai moyen (h)")} value={num(k.avg_turnaround_hours)} icon={Clock} tone="slate" />
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mt-5">
            <Card title={t("Tendance des échantillons")}>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.trend}>
                  <defs><linearGradient id="lg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0284C7" stopOpacity={0.3} /><stop offset="95%" stopColor="#0284C7" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend />
                  <Area type="monotone" dataKey="received" name="Reçus" stroke="#0284C7" fill="url(#lg)" strokeWidth={2} />
                  <Area type="monotone" dataKey="tested" name="Analysés" stroke="#F05A28" fillOpacity={0} strokeWidth={2} />
                  <Area type="monotone" dataKey="positive" name="Positifs" stroke="#DC2626" fillOpacity={0} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Card>
            <Card title={t("Taux de positivité par maladie")}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={positivity} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="positivite" name="Positivité %" fill="#DC2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card title={t("Échantillons par Zone de Santé")}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.by_zone} margin={{ bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="health_zone" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={60} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend />
                  <Bar dataKey="received" name="Reçus" fill="#0284C7" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="tested" name="Analysés" fill="#F05A28" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="positive" name="Positifs" fill="#DC2626" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm mt-5 overflow-hidden" data-testid="lab-history">
            <div className="p-4 border-b border-slate-100"><h3 className="text-sm font-semibold text-slate-800">{t("Rapports récents")}</h3></div>
            <div className="overflow-x-auto max-h-[420px]">
              <Table>
                <TableHeader><TableRow className="bg-slate-50">
                  <TableHead>{t("Date")}</TableHead><TableHead>{t("Zone")}</TableHead><TableHead>{t("Maladie")}</TableHead>
                  <TableHead>{t("Technicien")}</TableHead><TableHead className="text-right">{t("Voir")}</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(reps?.items || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">{t("Aucun rapport.")}</TableCell></TableRow>
                  ) : reps.items.map((r) => (
                    <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`lab-row-${r.id}`}>
                      <TableCell className="font-mono text-xs">{r.report_date}</TableCell>
                      <TableCell className="text-sm">{r.health_zone}<div className="text-xs text-slate-500">{r.province}</div></TableCell>
                      <TableCell className="text-sm">{r.disease ? t(r.disease) : "-"}</TableCell>
                      <TableCell className="text-sm">{r.technician_name}</TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => setDetail(r)} data-testid={`lab-view-${r.id}`}><Eye className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle>{t("Détail du rapport labo")}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">{t("Localisation")}: <b>{detail.health_zone}</b> · {detail.province} · {detail.report_date}</p>
            <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
              <div><p className="text-xs text-slate-500">{t("Laboratoire")}</p><p className="font-medium">{detail.laboratory || "-"}</p></div>
              <div><p className="text-xs text-slate-500">{t("Maladie")}</p><p className="font-medium">{detail.disease ? t(detail.disease) : "-"}</p></div>
            </div>
            {detail.activities?.length > 0 && <div className="mt-2"><p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Activités")}</p>
              <div className="flex flex-wrap gap-1.5">{detail.activities.map((a) => <span key={a} className="text-xs bg-[#E0F2FE] text-[#0284C7] px-2 py-0.5 rounded">{actMap[a] || a}</span>)}</div></div>}
            <div className="mt-2"><p className="text-xs font-semibold uppercase text-slate-400 mb-1">{t("Chiffres")}</p>
              <div className="grid grid-cols-3 gap-2">{Object.entries(detail.metrics || {}).filter(([, v]) => v > 0).map(([kk, v]) => (
                <div key={kk} className="bg-slate-50 rounded-lg px-2 py-1.5"><p className="text-[10px] text-slate-400 leading-tight">{kk}</p><p className="font-semibold">{v}</p></div>
              ))}</div></div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
